import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Link2, Unlink, RefreshCw, Users, BookOpen, 
  CheckCircle, XCircle, Loader2, Download, School, AlertCircle
} from 'lucide-react';

const GoogleClassroomIntegration = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [courses, setCourses] = useState([]);
  const [syncing, setSyncing] = useState({});
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/google-classroom/status');
      setStatus(res.data);
      if (res.data.connected) {
        fetchCourses();
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await api.get('/google-classroom/courses');
      setCourses(res.data.courses);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/google-classroom/authorize', { params: { return_path: '/teacher/google-classroom' } });
      if (res.data?.authorization_url) {
        window.location.assign(res.data.authorization_url);
      }
    } catch (error) {
      toast.error('Failed to start Google Classroom connect');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Classroom?')) return;
    
    try {
      await api.post('/google-classroom/disconnect');
      toast.success('Google Classroom disconnected');
      setStatus({ connected: false });
      setCourses([]);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSync = async (courseId) => {
    setSyncing(prev => ({ ...prev, [courseId]: true }));
    try {
      const res = await api.post(`/google-classroom/courses/${courseId}/sync-roster`);
      toast.success(`Synced ${res.data.students_synced} students`);
    } catch (error) {
      toast.error('Failed to sync course');
    } finally {
      setSyncing(prev => ({ ...prev, [courseId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/teacher">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <School className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-slate-800">Google Classroom</h1>
                  <p className="text-sm text-slate-500">Sync your classes and students</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Connection Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {status?.connected ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-slate-400" />
                  )}
                  Connection Status
                </CardTitle>
                <CardDescription>
                  {status?.connected 
                    ? `Connected as ${status.email}`
                    : 'Connect your Google Classroom account to sync classes'
                  }
                </CardDescription>
              </div>
              {status?.connected ? (
                <Button variant="outline" onClick={handleDisconnect} className="text-red-600">
                  <Unlink className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              ) : (
                <Button onClick={handleConnect} disabled={connecting} className="bg-blue-600 hover:bg-blue-700">
                  {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Connect Google Classroom
                </Button>
              )}
            </div>
          </CardHeader>
          {status?.connected && status?.last_sync && (
            <CardContent>
              <p className="text-sm text-slate-500">
                Last synced: {new Date(status.last_sync).toLocaleString()}
              </p>
            </CardContent>
          )}
        </Card>

        {/* Mock Data Notice */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Demo Mode</p>
              <p className="text-sm text-amber-700">
                This is a demonstration using mock Google Classroom data. In production, 
                you would connect with your actual Google account via OAuth.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Courses List */}
        {status?.connected && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg text-slate-800">Your Courses</h2>
              <Button variant="outline" size="sm" onClick={fetchCourses}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>

            {courses.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No courses found in Google Classroom</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {courses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                              <BookOpen className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-slate-800">{course.name}</h3>
                              <p className="text-sm text-slate-500">{course.section}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  <Users className="w-3 h-3 mr-1" />
                                  {course.student_count} students
                                </Badge>
                                <Badge 
                                  variant={course.state === 'ACTIVE' ? 'default' : 'secondary'}
                                  className="text-xs"
                                >
                                  {course.state}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleSync(course.id)}
                            disabled={syncing[course.id]}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`sync-${course.id}`}
                          >
                            {syncing[course.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Download className="w-4 h-4 mr-2" />
                            )}
                            Sync to Zazu
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Not Connected State */}
        {!status?.connected && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <School className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="font-bold text-xl text-slate-800 mb-2">
                Connect Google Classroom
              </h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Import your classes and students from Google Classroom to easily assign 
                Zazu activities and track their progress.
              </p>
              <Button 
                onClick={handleConnect} 
                disabled={connecting}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" />
                )}
                Connect Now
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default GoogleClassroomIntegration;
